import { useEffect, useMemo, useRef, useState } from "react";
import { squarify } from "../../lib/squarify";
import { AtlasTile } from "./atlas-tile";
import type { AtlasEntry, LaidAtlasEntry } from "./types";

interface Props {
  entries: AtlasEntry[];
}

// Aspect-aware squarified layout for the full-territory atlas. Resize is
// debounced because N1 has ~3.4k tiles and re-squarifying on every
// ResizeObserver tick visibly hitches under live drag.
const RESIZE_DEBOUNCE_MS = 100;

export function AtlasMap({ entries }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // null until the first ResizeObserver measurement lands. Avoids
  // squarifying against a hardcoded aspect guess and then snap-relaying
  // ~3.4k tiles when the real container dims arrive.
  const [aspect, setAspect] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let hasLaidOut = false;
    const apply = (width: number, height: number) => {
      const ratio = width / height;
      const h = Math.sqrt(6000 / ratio);
      const w = h * ratio;
      setAspect({ w, h });
    };
    const ro = new ResizeObserver(([e]) => {
      if (!e) return;
      const { width, height } = e.contentRect;
      if (width <= 0 || height <= 0) return;
      // First measurement paints immediately so the user doesn't stare
      // at a blank container for RESIZE_DEBOUNCE_MS on initial load;
      // subsequent measurements (live drag, devtools toggle) coalesce.
      if (!hasLaidOut) {
        hasLaidOut = true;
        apply(width, height);
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => apply(width, height), RESIZE_DEBOUNCE_MS);
    });
    ro.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  const laid = useMemo<LaidAtlasEntry[]>(() => {
    if (entries.length === 0 || aspect === null) return [];
    const total = entries.reduce(
      (s, e) => s + Math.max(0, Number(e.lemma_count)),
      0,
    );
    // Floor every weight at 0.5% of total so corpus-zero entries are
    // still legible parcels rather than zero-area slivers.
    const floor = Math.max(1, total * 0.005);
    const weighted = entries.map((e) => ({
      ...e,
      weight: Math.max(floor, Number(e.lemma_count)),
    }));
    const out = squarify(weighted, aspect);
    return out.map((r) => ({
      ...r,
      x: (r.x / aspect.w) * 100,
      y: (r.y / aspect.h) * 100,
      w: (r.w / aspect.w) * 100,
      h: (r.h / aspect.h) * 100,
    }));
  }, [entries, aspect]);

  return (
    <div ref={containerRef} className="n5-map absolute inset-0">
      {laid.map((entry, i) => (
        <AtlasTile
          key={`${entry.jmdict_seq}::${entry.kana}::${entry.kanji}`}
          entry={entry}
          rank={i + 1}
        />
      ))}
    </div>
  );
}
